import { useState } from "react";
import {
  Calculator, ChevronDown, ShieldCheck, TrendingUp, Handshake, Ruler,
  Building2, Split, Landmark, Sparkles, Info,
} from "lucide-react";

const SECTIONS = [
  {
    id: "underwriting",
    icon: ShieldCheck,
    title: "Underwriting Model",
    tag: "Hard Caps · Profit Protected",
    blurb:
      "Every deal is stress-tested against caps expressed as a percentage of GDV (Gross Development Value). All-in cost is hard-capped at 90% of GDV, so a 10% net margin is the PROTECTED FLOOR, not the target.",
    where: "Property Analysis → Underwriting Model",
    rows: [
      { label: "All-In Cost Cap", formula: "GDV × 90%", note: "Maximum total cost the deal may carry" },
      { label: "Protected Net Profit (floor)", formula: "GDV − All-In Cap  =  GDV × 10%", note: "Worst-case margin if delivered at the cap" },
      { label: "Hard Construction Cap", formula: "GDV × 50%", note: "Ceiling on hard construction budget" },
      { label: "Land Purchase Cap", formula: "GDV × 15%", note: "Maximum supportable land price" },
      { label: "Builder / GC Fee", formula: "12% of hard construction cost (editable)", note: "Sits inside the 50% construction cap" },
      { label: "Total Construction", formula: "min( stored costs OR projected + builder fee , Construction Cap )", note: "Never exceeds the 50% cap" },
      { label: "Status", formula: "PASS when All-In ≤ All-In Cap", note: "Over-cap shows as red variance" },
    ],
  },
  {
    id: "returns",
    icon: TrendingUp,
    title: "Return Potential",
    tag: "Floor → Upside",
    blurb:
      "Shows the protected floor and the realistic path to higher margins when construction beats the cap and/or GDV comes in above the conservative estimate.",
    where: "Property Analysis → Return Potential",
    rows: [
      { label: "Floor Margin", formula: "10%", note: "All-in delivered at 90% of GDV" },
      { label: "Base Case", formula: "≈ 15%", note: "All-in delivered near 85% of GDV" },
      { label: "Upside Target", formula: "20% – 25%", note: "Value engineering + GDV upside" },
      { label: "Profit at margin m", formula: "GDV × m", note: "Implied all-in = GDV × (1 − m)" },
    ],
  },
  {
    id: "offer",
    icon: Handshake,
    title: "Offer vs. List",
    tag: "Max supportable offer",
    blurb: "Compares the seller's list price against the maximum land price the underwriting supports.",
    where: "Property Analysis → Offer vs. List",
    rows: [
      { label: "Max Offer (underwriting)", formula: "GDV × 15%", note: "Same basis as the Land Purchase cap" },
      { label: "Land Savings", formula: "Max Offer − List Price", note: "Shown when list price is below the cap" },
    ],
  },
  {
    id: "psf",
    icon: Ruler,
    title: "Cost per Sq Ft",
    tag: "Buildability check",
    blurb: "Sanity-checks the construction budget on a dollars-per-square-foot basis against the proposed home size.",
    where: "Property Analysis → Cost per Sq Ft",
    rows: [
      { label: "Construction Cap", formula: "GDV × 50%", note: "Hard construction ceiling" },
      { label: "Cost / SF", formula: "Total Construction ÷ Living SF", note: "Delivered build cost per square foot" },
      { label: "Margin Tiers", formula: "≥ 15% Strong · ≥ 5% Marginal · else Thin/Loss", note: "Color-coded status" },
    ],
  },
  {
    id: "propertytype",
    icon: Building2,
    title: "Property Type & Units",
    tag: "Per-unit economics",
    blurb:
      "A development is treated as ONE building/site. Totals come from the property's own underwriting; per-unit figures are the totals divided by the unit count.",
    where: "Property Analysis → Property Type",
    rows: [
      { label: "Units", formula: "SFR = 1 · Duplex = 2 · Triplex = 3 · Fourplex = 4 · others user-set", note: "Locked for the fixed types" },
      { label: "GDV / Unit", formula: "Total GDV ÷ Units", note: "" },
      { label: "Cost / Unit", formula: "All-In Cost ÷ Units", note: "" },
      { label: "Unit Size", formula: "Living SF ÷ Units", note: "" },
      { label: "Attached — Net Sellable", formula: "Gross Building SF × Efficiency (85% default)", note: "Common-area loss factor" },
      { label: "Attached — GDV basis", formula: "stored comp GDV → avg comp $/SF → $350/SF × Net Sellable", note: "First available basis wins" },
    ],
  },
  {
    id: "subdivision",
    icon: Split,
    title: "Subdivision / Multi-Lot",
    tag: "Blended deal economics",
    blurb:
      "When a deal is split into multiple lots, deal-level totals are the blend of all lots. Land is counted ONCE for the whole site; per-home costs scale with the lot count.",
    where: "Property Analysis → Subdivision / Multi-Lot",
    rows: [
      { label: "Deal GDV", formula: "Σ (per-lot GDV)", note: "Sum across all lots" },
      { label: "Direct Cost", formula: "Σ (per-lot build cost)", note: "Sum across all lots" },
      { label: "Site / Land", formula: "counted once, allocated evenly across lots", note: "Not multiplied per lot" },
      { label: "All-In", formula: "Direct Cost + Site", note: "" },
      { label: "Net Profit", formula: "Deal GDV − All-In", note: "" },
      { label: "Margin", formula: "Net Profit ÷ Deal GDV", note: "" },
    ],
  },
  {
    id: "landflip",
    icon: Landmark,
    title: "Land-Flip Valuation (Angela)",
    tag: "Comp-driven · cost sets returns",
    angela: true,
    blurb:
      "Values subdivided lots sold as entitled land. RULE: market value is NEVER set from what you paid — Angela derives it from recent comparable entitled/buildable lot sales. Your acquisition cost only establishes basis and calculates returns.",
    where: "Property Analysis → Per-Parcel Subdivision Economics",
    rows: [
      { label: "Land Basis / Parcel", formula: "Land Purchase ÷ Lots", note: "Even cost basis before entitlement" },
      { label: "Uplift Benchmark (secondary)", formula: "Angela-researched local cost-uplift %", note: "Sanity check only — does not set value" },
      { label: "Cost-Based Benchmark", formula: "Land Basis × (1 + Uplift %)", note: "Cross-check against market value" },
      { label: "Market Value / Parcel (PRIMARY)", formula: "comparable entitled-lot sales, adjusted for size, utilities, location, access, views, condition & entitlement status", note: "The real valuation" },
      { label: "Total Entitled Market Value", formula: "Market Value / Parcel × Lots", note: "" },
      { label: "Entitlement + Carry Costs", formula: "Angela est. (engineering, permitting, legal, closing, carry)", note: "" },
      { label: "Total Investment", formula: "Land Purchase + Entitlement + Carry", note: "" },
      { label: "Net Land-Flip Profit", formula: "Total Entitled Market Value − Total Investment", note: "" },
      { label: "ROI on Total Investment", formula: "Net Profit ÷ Total Investment", note: "Return on capital deployed" },
      { label: "Margin on Sale", formula: "Net Profit ÷ Total Entitled Market Value", note: "" },
      { label: "Rounding", formula: "all figures rounded to the nearest $1,000", note: "Clean, even numbers" },
    ],
  },
  {
    id: "import",
    icon: Sparkles,
    title: "Angela Web Import",
    tag: "Auto-filled from comps",
    angela: true,
    blurb: "When you import public-record data, Angela fills blank fields and derives a few numbers from the three comparable sales she finds.",
    where: "Any record → Actions → Import from Angela",
    rows: [
      { label: "Estimated New-Construction GDV", formula: "avg of the 3 comp sale prices", note: "Only when GDV is empty" },
      { label: "Proposed Home Size", formula: "avg of the 3 comp living SF", note: "" },
      { label: "Proposed Beds / Baths", formula: "avg of the 3 comps", note: "" },
    ],
  },
];

function CalcSection({ section }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`calc-section-${section.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-accent/40 transition-colors"
        data-testid={`calc-toggle-${section.id}`}
      >
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${section.angela ? "bg-primary/15 text-primary" : "bg-secondary text-foreground"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-semibold text-foreground">{section.title}</span>
            {section.tag && <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">{section.tag}</span>}
          </span>
          <span className="block text-[11px] text-muted-foreground mt-0.5 truncate">{section.where}</span>
        </span>
        <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1" data-testid={`calc-body-${section.id}`}>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">{section.blurb}</p>
          <div className="rounded-lg border border-border/60 divide-y divide-border/50 overflow-hidden">
            {section.rows.map((r) => (
              <div key={r.label} className="grid grid-cols-1 md:grid-cols-12 gap-1.5 md:gap-4 px-4 py-3">
                <div className="md:col-span-4 text-sm font-medium text-foreground">{r.label}</div>
                <div className="md:col-span-5">
                  <code className="text-[13px] font-mono text-primary bg-secondary/50 rounded px-2 py-0.5 inline-block leading-relaxed">{r.formula}</code>
                </div>
                <div className="md:col-span-3 text-[12px] text-muted-foreground md:pt-0.5">{r.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Calculations() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-8" data-testid="calculations-page">
      <div className="flex items-start gap-3 mb-2">
        <span className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Calculator className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Calculations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Exactly how Arqitek and Angela compute every number — the formulas, inputs, and where each appears.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-secondary/20 px-4 py-3 mb-6" data-testid="calc-intro-note">
        <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          All underwriting caps are expressed as a percentage of GDV. Cards marked with a teal icon are powered by Angela's research; everything else is computed live in the app.
        </p>
      </div>

      <div className="space-y-4">
        {SECTIONS.map((s) => (
          <CalcSection key={s.id} section={s} />
        ))}
      </div>
    </div>
  );
}
